// Frontend mirror of appquickchat/logging_utils/credential_redaction.py, shared by every analytics guard so they cannot drift apart.

export const REDACTED_VALUE = '<redacted>';

// REDACTED_PAYLOAD_KEYS, whole names only so `promo_code` and `codes` survive. `metadata_value` is
// merchant data rather than a credential; datadogRumFilter.ts redacts it on the RUM side.
export const CREDENTIAL_BEARING_QUERY_PARAMS = new Set([
  'access_token',
  'authorization',
  'client_secret',
  'code',
  'id_token',
  'metadata_value',
  'refresh_token',
  'secret',
  'signed_request',
  'state',
  'token',
]);

// REDACTED_PATH_PREFIXES: the segment that follows one of these is a bearer secret.
export const CEREMONY_PATH_SEGMENTS = [
  'link-channel',
  'authorize-marketing-send',
  'confirm-email-change',
];

// A `next` nests a URL of its own, and that URL can nest another.
export const MAX_NESTED_URL_LAYERS = 3;

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;
const PLACEHOLDER_ORIGIN = 'http://redaction.invalid';

function redactCeremonySecret(pathname: string): string {
  const segments = pathname.split('/');
  return segments
    .map((segment, index) =>
      index > 0 && CEREMONY_PATH_SEGMENTS.includes(segments[index - 1]) ? REDACTED_VALUE : segment,
    )
    .join('/');
}

// Strips credentials from a URL: its own query, a ceremony secret in its path, and both again inside any URL it carries as a parameter value.
export function redactCredentialsInUrl(value: string, layer = 0): string {
  const absolute = ABSOLUTE_URL_PATTERN.test(value);
  // `//host/path` also starts with `/`, and resolving it against the placeholder origin would drop the host.
  const protocolRelative = !absolute && value.startsWith('//');
  if (!absolute && !value.startsWith('/')) {
    return value;
  }

  let url: URL;
  try {
    url = new URL(value, PLACEHOLDER_ORIGIN);
  } catch {
    return value;
  }

  url.pathname = redactCeremonySecret(url.pathname);
  // Rebuilt rather than set() in place, which drops every repeat of a key and rewrites `?tag=a&tag=b` as `?tag=b`.
  const redacted = new URLSearchParams();
  url.searchParams.forEach((paramValue, paramKey) => {
    if (CREDENTIAL_BEARING_QUERY_PARAMS.has(paramKey.toLowerCase())) {
      redacted.append(paramKey, REDACTED_VALUE);
    } else {
      redacted.append(
        paramKey,
        layer < MAX_NESTED_URL_LAYERS ? redactCredentialsInUrl(paramValue, layer + 1) : paramValue,
      );
    }
  });
  url.search = redacted.toString();

  const relativePath = url.pathname + url.search + url.hash;
  if (absolute) {
    return url.toString();
  }
  return protocolRelative ? `//${url.host}${relativePath}` : relativePath;
}
